import { useState } from "react";

/*
|--------------------------------------------------------------------------
| Menu Component
|--------------------------------------------------------------------------
| Handles:
| • Hamburger menu
| • Spot search
| • Nearby spot discovery
| • Navigation back to map
*/
function Menu({ menuOpen, setMenuOpen, spots, nearbySpots, setSelectedSpot, mapRef }) {

  /*
  |--------------------------------------------------------------------------
  | Local UI State
  |--------------------------------------------------------------------------
  | menuView: controls which panel is shown
  | searchQuery: text typed in search box
  */
  const [menuView, setMenuView] = useState("menu");
  const [searchQuery, setSearchQuery] = useState("");


  /*
  |--------------------------------------------------------------------------
  | Search Filtering
  |--------------------------------------------------------------------------
  | Filters spot list based on search text
  | Limits results to 8 items for usability
  */
  const filteredSpots = spots
    ?.filter((spot) =>
      spot.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, 8);


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */
  return (
    <>
      {/* ----------------------------------------------------- */}
      {/* Hamburger Button (only visible when menu is closed) */}
      {/* ----------------------------------------------------- */}
      {!menuOpen && (
        <div style={menuContainer}>
          <button
            style={menuButton}
            onClick={() => setMenuOpen(true)}
          >
            ☰
          </button>
        </div>
      )}


      {/* ----------------------------------------------------- */}
      {/* Menu Open State */}
      {/* ----------------------------------------------------- */}
      {menuOpen && (
        <>
          {/* Overlay closes the menu when clicked */}
          <div
            style={menuOverlay}
            onClick={() => {
              setSearchQuery("");
              setMenuView("menu");
              setMenuOpen(false);
              document.activeElement.blur(); // closes mobile keyboard
            }}
          />

          <div style={menuPanel}>

            {/* ================================================= */}
            {/* MAIN MENU VIEW                                   */}
            {/* ================================================= */}
            {menuView === "menu" && (
              <>
                <button
                  style={menuItem}
                  onClick={() => setMenuView("search")}
                >
                  🔍 Search Spots
                </button>

                <button
                  style={menuItem}
                  onClick={() => setMenuView("nearby")}
                >
                  📍 Nearby Spots
                </button>

                <button style={menuItem}>
                  ⭐ Favorites (Coming Soon!)
                </button>

                <button
                  style={menuItem}
                  onClick={() => setMenuOpen(false)}
                >
                  🗺 Explore Map
                </button>
              </>
            )}


            {/* ================================================= */}
            {/* SEARCH VIEW                                      */}
            {/* ================================================= */}
            {menuView === "search" && (
              <>
                {/* Back button */}
                <button
                  style={menuItem}
                  onClick={() => setMenuView("menu")}
                >
                  ← Back
                </button>

                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search spots..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={searchInput}
                />

                {/* Empty Search Message */}
                {searchQuery.length > 0 && filteredSpots.length === 0 && (
                  <div style={emptyMessage}>
                    No spots found.
                  </div>
                )}

                {/* Search Results */}
                {searchQuery.length > 0 && filteredSpots.map((spot) => (
                  <button
                    key={spot.id}
                    style={menuItem}
                    onClick={() => {

                      /* Move map to selected spot */
                      mapRef.current.panTo({
                        lat: spot.lat,
                        lng: spot.lng
                      });

                      mapRef.current.setZoom(15);

                      /* Open spot sheet */
                      setSelectedSpot(spot);

                      /* Reset menu */
                      setSearchQuery("");
                      setMenuView("menu");
                      setMenuOpen(false);

                      document.activeElement.blur();
                    }}
                  >
                    📍 {spot.name}
                  </button>
                ))}
              </>
            )}


            {/* ================================================= */}
            {/* NEARBY SPOTS VIEW                                */}
            {/* ================================================= */}
            {menuView === "nearby" && (
              <>
                {/* Back button */}
                <button
                  style={menuItem}
                  onClick={() => setMenuView("menu")}
                >
                  ← Back
                </button>

                {/* Empty Nearby Message */}
                {nearbySpots.length === 0 && (
                  <div style={emptyMessage}>
                    No spots within 5 miles.
                  </div>
                )}

                {/* Nearby Spot List */}
                {nearbySpots.map((spot) => (
                  <button
                    key={spot.id}
                    style={menuItem}
                    onClick={() => {

                      mapRef.current.panTo({
                        lat: spot.lat,
                        lng: spot.lng
                      });

                      setSelectedSpot(spot);
                      setMenuOpen(false);
                    }}
                  >
                    📍 {spot.name}

                    {/* Distance label */}
                    <div style={{
                      fontSize: "14px",
                      opacity: 0.7,
                      marginTop: "4px"
                    }}>
                      {spot.distance.toFixed(1)} miles away
                    </div>

                  </button>
                ))}
              </>
            )}

          </div>
        </>
      )}
    </>
  );
}


/*
|--------------------------------------------------------------------------
| Styles
|--------------------------------------------------------------------------
| UI styling for menu elements
*/

const menuContainer = {
  position: "fixed",
  top: "40px",
  right: "20px",
  padding: "16px 18px",
  background: "rgba(0,0,0,0.55)",
  backdropFilter: "blur(10px)",
  borderRadius: "14px",
  zIndex: 1000,
};

const menuButton = {
  fontSize: "28px",
  background: "none",
  border: "none",
  color: "white",
  cursor: "pointer",
};

const menuPanel = {
  position: "fixed",
  top: "95px",
  right: "20px",
  background: "rgba(28,28,30,0.9)",
  backdropFilter: "blur(20px)",
  borderRadius: "14px",
  padding: "12px",
  zIndex: 20000,
  boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
  minWidth: "180px",
};

const menuOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 15000,
};

const menuItem = {
  display: "block",
  width: "100%",
  padding: "16px",
  marginBottom: "10px",
  borderRadius: "12px",
  background: "rgba(255,255,255,0.05)",
  color: "white",
  border: "none",
  textAlign: "left",
  fontSize: "20px",
  fontWeight: "600",
};

const searchInput = {
  width: "100%",
  padding: "14px",
  marginBottom: "10px",
  borderRadius: "10px",
  border: "none",
  background: "rgba(255,255,255,0.08)",
  color: "white",
  fontSize: "16px",
  outline: "none",
};

const emptyMessage = {
  padding: "18px",
  textAlign: "center",
  fontSize: "16px",
  color: "#c7c7cc",
  opacity: 0.8,
};

export default Menu;